<i18n>
{
	"en": {
		"clearSelection": "Clear selection",
		"close": "Close",
		"goToToday": "Go to today",
		"selectDecade": "Select Decade",
		"selectMonth": "Select Month",
		"selectYear": "Select Year"
	},
	"no": {
		"clearSelection": "Tøm valg",
		"close": "Lukk",
		"goToToday": "Gå til i dag",
		"selectDecade": "Velg Tiår",
		"selectMonth": "Velg Måned",
		"selectYear": "Velg År"
	}
}
</i18n>
<template>
	<t-popover
		:showPopover="popoverIsVisible"
		:marginY="6"
		:style="getStyle"
		position="bottom"
		:width="260"
		v-bind="$attrs"
	>
		<t-input-wrapper
			slot="target"
			:style="useMinWidth ? {width: '110px'} : {}"
			:label="label"
			:description="description"
			:hint="hint"
			:isInvalid="isInvalid"
			:validationMessage="validationMessage"
			:warningMessage="warningMessage"
		>
			<t-text-input-button
				ref="input"
				:placeholder="placeholder"
				:value="internalValue"
				:size="size"
				:disabled="disabled"
				:disableTabbing="disableTabbing"
				:disableButtonTabbing="disableButtonTabbing"
				icon="calendar"
				iconSize="sm"
				width="100%"
				@change="onTextInputChanged"
				@button:click="popoverToggle"
				@keydown.enter="onEnterDown"
				@keydown.tab="onTabDown"
				@button:blur="popoverSetHidden"
				@keydown="onButtonKeyDown"
				@focus="$emit('focus', $event) && $event.target.select()"
				@blur="onBlur"
			/>
		</t-input-wrapper>
		<date-picker
			id="date-time-input"
			ref="datePicker"
			:value="internalValue"
			:disabled="disabled"
			:wrap="true"
			:config="options"
			style="width:100%;"
			@input="onTextInputChanged"
			@keydown.enter="onEnterDown"
			@keydown.tab="onTabDown"
		/>
		<t-button appearance="minimal" class="today-btn" @click="setToday">{{$t("goToToday")}}</t-button>

	</t-popover>
</template>


<script>
import DatePicker from 'vue-bootstrap-datetimepicker';
//import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import './scss/_date-picker.scss';
import TTextInputButton from '@/components/evergreen/inputs/TTextInputButton';
import TInputWrapper from '@/components/evergreen/inputs/TInputWrapper';
import TPopover from '@/components/evergreen/overlays/TPopover';
import { displayDateFormat } from '@/core';
import formMixin from './formMixin';
import inputWrapperMixin from './inputWrapperMixin';
import popoverMixin from './popoverMixin';
import dateMixin from './dateMixin';
import moment from 'moment';
moment.updateLocale('en', { week: { dow: 1 } });
export default {
	name: 'TDatePicker',
	components: {
		DatePicker,
		TTextInputButton,
		TInputWrapper,
		TPopover
	},
	mixins: [ formMixin, inputWrapperMixin, popoverMixin, dateMixin ],
	inheritAttrs: false,
	props: {
		/**
		 * Format of date displayed in date picker. Current value must match this format.
		 */
		dateFormat: {
			type: String,
			default: displayDateFormat
		},
		headerFormat: {
			type: String,
			default: 'MMMM YYYY'
		},
		appearance: String,
		intent: String,
		disableTabbing: {
			type: Boolean,
			default: false
		},
		disableButtonTabbing: {
			type: Boolean,
			default: false
		},
		disableTimeoutForEnterEvent: {
			type: Boolean,
			default: false
		},
		useMinWidth: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return	{
			internalValue: undefined,
		};
	},
	computed: {
		options() {
			return {
				minDate: this.minDate === undefined ? false : this.minDate,
				maxDate: this.maxDate === undefined ? false : this.maxDate,
				allowInputToggle: true,
				format: this.dateFormat,
				dayViewHeaderFormat: this.headerFormat,
				useCurrent: false,
				debug: false,
				showClose: false,
				showClear: false,
				showTodayButton: false,
				toolbarPlacement: 'top',
				locale: this.getLocale(),
				inline: true,
				icons: {
					previous: 'fas fa-chevron-left fa-sm',
					next: 'fas fa-chevron-right fa-sm',
					close: 'fas fa-times fa-sm',
					clear: 'fas fa-trash-alt fa-sm',
					today: 'fas fa-calendar-check fa-sm'
				},
				tooltips: {
					today: this.$t('goToToday'),
					clear: this.$t('clearSelection'),
					close: this.$t('close'),
					selectMonth: this.$t('selectMonth'),
					selectYear: this.$t('selectYear'),
					selectDecade: this.$t('selectDecade')
				},
			};
		},
		getStyle() {
			let width = `width: ${this.width};`;
			return width;
		},
		isValid() {
			if (this.invalid) return 'is-invalid';
			return '';
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(newValue) {
				this.internalValue = newValue;
			}
		}
	},
	mounted() {
		this.internalValue = this.value;
	},
	methods: {
		onEnterDown(event) {
			/**
			 * Fired when Enter key is pressed when date picker is focused. disableTimeoutForEnterEvent is needed to
			 * use stopPropagation in a parent component.
			 * @event enter
			 */
			if (this.disableTimeoutForEnterEvent) {
				this.$emit('enter', event);
			} else {
				setTimeout(() => this.$emit('enter', event), 0);
			}
		},
		onTabDown(event) {
			this.$emit('tab', event);
		},
		onTextInputChanged(value) {
			let date = moment(value, this.dateFormat);
			if (date.isValid()) {
				this.onInputChanged(date.format(this.dateFormat));
			} else {
				this.onInputChanged(value);
			}
		},
		onInputChanged(value) {
			/**
			 * Fired immediately when input value is changed.
			 * @event input
			 * @type {object}
			 */
			if (this.internalValue === value) {
				this.$forceUpdate(); // Vue won't be reactive if value is same, force re-render
				return;
			}
			this.internalValue = value;

			this.$emit('input', value);
			this.popoverSetHidden();
		},
		setToday() {
			this.onTextInputChanged(moment());
		},
		getLocale() {
			let lang = localStorage.getItem('locale');
			if (lang === 'no') {
				lang = 'nb';
			}
			return lang;
		},
		focus() {
			this.$refs.input.focus();
		},
		blur() {
			this.$refs.input.blur();
		},
		onButtonKeyDown(event) {
			let hotkeys = ['KeyT', 'KeyN', 'ArrowUp', 'ArrowDown'];
			if (hotkeys.includes(event.code)) {
				event.preventDefault();
				this[`on${event.code}`]();
			}
			this.$emit('button:keydown', event);
		},
		onKeyT() {
			this.setToday();
		},
		onKeyN() {
			this.setToday();
		},
		onArrowUp() {
			let newValue = moment(this.internalValue, this.dateFormat).add(1, 'd');
			this.onTextInputChanged(newValue);
		},
		onArrowDown() {
			let newValue = moment(this.internalValue, this.dateFormat).add(-1, 'd');
			this.onTextInputChanged(newValue);
		},
	},

};
</script>

<style>
	.today-btn > div {
		font-weight: bold
	}
</style>

<docs>
## Examples

### Basic Usage

```jsx
<div style="display: inline-grid; grid-row-gap: 10px; grid-template-rows: repeat(3, auto); width: 100%;">
	<t-date-picker width="140px" dateFormat="DD.MM.YYYY"></t-date-picker>
	<t-date-picker dateFormat="DD.MM.YYYY"></t-date-picker>
	<t-date-picker width="100%" dateFormat="DD.MM.YYYY"></t-date-picker>
	<t-date-picker dateFormat="DD.MM.YYYY" disabled></t-date-picker>
</div>
```
</docs>
